import {
  CreateGeofenceStyleModel,
  EditGeofenceStyleModel,
  GeofenceStyleUnionModel,
  useCreateGeofenceStyleMutation,
  useEditGeofenceStyleMutation,
  useGetGeofenceStyleQuery,
} from '@casops/store/apis/geofenceStylesApi';
import { useAppSelector } from '@casops/store/hooks';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { useMemo } from 'react';

export interface GeofenceStyleFormModel extends Omit<GeofenceStyleUnionModel, 'used'> {}

const GeofencStyleDefaults: GeofenceStyleFormModel = {
  id: -1,
  name: '',
  desc: '',
  geos: [],
  fill: {
    defaultColour: '#66D575',
    activeColour: '#F24822',
    pattern: 1,
    opacity: 50,
  },
  line: {
    colour: '#000000',
    pattern: 1,
    opacity: 50,
    width: 1,
  },
};

export const useManageGeofenceStyle = () => {
  const { sidePanel, sidePanelModelId, sidePanelEditMode } = useAppSelector(state => state.global);

  const modelId = Number(sidePanelModelId);

  const { currentData: item, isSuccess } = useGetGeofenceStyleQuery(
    { id: modelId },
    {
      skip: sidePanel !== SidePanel.ManageGeofenceStyle || !sidePanelEditMode || isNaN(modelId),
      refetchOnMountOrArgChange: 10,
    },
  );

  const [initialValues, isLoaded] = useMemo<[GeofenceStyleFormModel, boolean]>(() => {
    let initialValues: GeofenceStyleFormModel = { ...GeofencStyleDefaults };
    let isLoaded = false;

    // Confirm that the Edit form data-fetching requirments are met.
    if (sidePanelEditMode && !isNaN(modelId) && isSuccess && item) {
      isLoaded = true;
      // Override the default form values with the existing item data.
      initialValues = { ...GeofencStyleDefaults, ...item };
    }
    // The create form has no further data-fetching requirements.
    else if (!sidePanelEditMode) {
      isLoaded = true;
    }

    return [initialValues, isLoaded];
  }, [modelId, sidePanelEditMode, isSuccess, item]);

  const [createItem] = useCreateGeofenceStyleMutation();

  const submitCreate = (value: CreateGeofenceStyleModel) => createItem(value).unwrap();

  const [patchItem] = useEditGeofenceStyleMutation();

  const submitPatch = (values: EditGeofenceStyleModel) => patchItem(values).unwrap();

  return {
    isLoaded,
    initialValues,
    submitCreate,
    submitPatch,
  };
};

export default useManageGeofenceStyle;
